import React, { useEffect, useState } from "react";
import { X, Settings, ChevronRight, Coins, Calculator, LogOut } from "lucide-react";
import { PinKeypad } from "../PinKeypad/PinKeypad";
import { Cashier } from "../../types/cashier.types";
import { loginWithPin } from "../../services/AuthService";
import { useAvailableCashiers } from "../../hooks/useAvailableCashiers";
import { UserSelector } from "./UserSelector";
import { ThermalPrinterSettings } from "./ThermalPrinterSettings";
import "./SettingsModal.css";

export default function SettingsModal({
    isOpen,
    view,
    setView,
    pinValue,
    setPinValue,
    isClosable,
    onClose,
    onSelectRegisterClosure,
    canRegisterClosure = true,
    onUserChange,
    onLoginError,
    enableCashChangeCalculator = true,
    onToggleCashChangeCalculator
}: {
    isOpen: boolean,
    view: 'menu' | 'change_user',
    setView: (v: 'menu' | 'change_user') => void,
    pinValue: string,
    setPinValue: React.Dispatch<React.SetStateAction<string>>,
    isClosable: boolean,
    onClose: () => void,
    onSelectRegisterClosure: () => void,
    /** Falso oculta la opcion de arqueo: el cajero no tiene arqueo_tienda_read ni arqueo_tienda_all. */
    canRegisterClosure?: boolean,
    onUserChange: (user: Cashier) => void,
    onLoginError?: (message: string) => void,
    enableCashChangeCalculator?: boolean,
    onToggleCashChangeCalculator?: (val: boolean) => void
}) {
    const [selectedCashier, setSelectedCashier] = useState<Cashier | null>(null);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { cashiers, isLoading, error, reload } = useAvailableCashiers(
        isOpen && view === 'change_user'
    );

    // Preselecciona el ultimo cajero de esta maquina; si no, el primero.
    useEffect(() => {
        if (cashiers.length === 0) return;
        setSelectedCashier(prev => {
            if (prev && cashiers.some(c => c.id === prev.id)) return prev;
            let lastId = '';
            try { lastId = localStorage.getItem('tpv_last_cashier_id') || ''; } catch { /* sin persistencia */ }
            return cashiers.filter(c => c.id === lastId)[0] || cashiers[0];
        });
    }, [cashiers]);

    useEffect(() => {
        if (pinValue.length !== 6 || !selectedCashier || isSubmitting) return;

        setIsSubmitting(true);
        setLoginError(null);

        loginWithPin(selectedCashier.id, pinValue)
            .then((user) => {
                try { localStorage.setItem('tpv_last_cashier_id', user.id); } catch { /* sin persistencia */ }
                setPinValue('');
                setIsSubmitting(false);
                onUserChange(user);
                onClose();
            })
            .catch((err) => {
                const message = err?.message || 'PIN incorrecto.';
                setPinValue('');
                setIsSubmitting(false);
                setLoginError(message);
                onLoginError?.(message);
            });
    }, [pinValue, selectedCashier, isSubmitting, onUserChange, onClose, setPinValue, onLoginError]);

    if (!isOpen) return null;

    return (
        <div className="settings-modal-overlay">
            <div className={`settings-modal-container ${view === 'menu' ? '' : 'settings-modal-container-pin'}`}>
                <div className="settings-modal-header">
                    <div className="settings-modal-title-group">
                        <Settings className="settings-icon" />
                        <h3 className="settings-modal-title">
                            {view === 'menu' ? 'Configuración' : (isClosable ? 'Cambio de Usuario' : 'Inicio de Sesión')}
                        </h3>
                    </div>
                    {isClosable && (
                        <button onClick={onClose} className="settings-modal-close-btn">
                            <X className="settings-close-icon" />
                        </button>
                    )}
                </div>

                <div className={`settings-modal-body ${view === 'menu' ? '' : 'settings-modal-body-no-padding'}`}>
                    {view === 'menu' ? (
                        <div className="settings-menu-list">
                            <button
                                onClick={() => setView('change_user')}
                                className="settings-menu-btn group"
                            >
                                <div className="settings-menu-btn-content">
                                    <div className="settings-menu-icon-box">
                                        <LogOut className="settings-menu-icon" />
                                    </div>
                                    <span className="settings-menu-text">{isClosable ? 'Cambio de Usuario' : 'Inicio de Sesión'}</span>
                                </div>
                                <ChevronRight className="settings-menu-chevron" />
                            </button>

                            {canRegisterClosure && <button
                                onClick={() => {
                                    onSelectRegisterClosure();
                                    onClose();
                                }}
                                className="settings-menu-btn group settings-menu-btn-mb"
                            >
                                <div className="settings-menu-btn-content">
                                    <div className="settings-menu-icon-box">
                                        <Coins className="settings-menu-icon" />
                                    </div>
                                    <span className="settings-menu-text">Arqueo y Cierre de Caja</span>
                                </div>
                                <ChevronRight className="settings-menu-chevron" />
                            </button>}

                            <div className="settings-menu-item">
                                <div className="settings-menu-btn-content">
                                    <div className="settings-menu-icon-box settings-menu-icon-active">
                                        <Calculator className="settings-menu-icon-active-color" />
                                    </div>
                                    <div className="settings-menu-text-group">
                                        <span className="settings-menu-text">Cálculo de Cambio</span>
                                        <span className="settings-menu-subtext">Teclado de efectivo en pagos</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onToggleCashChangeCalculator?.(!enableCashChangeCalculator)}
                                    className={`settings-toggle-btn ${enableCashChangeCalculator ? 'settings-toggle-on' : 'settings-toggle-off'}`}
                                >
                                    <div className="settings-toggle-circle" />
                                </button>
                            </div>

                            <ThermalPrinterSettings />
                        </div>
                    ) : (
                        <>
                            <UserSelector
                                cashiers={cashiers}
                                isLoading={isLoading}
                                error={error}
                                selectedCashier={selectedCashier}
                                isOpen={isSelectorOpen}
                                setIsOpen={setIsSelectorOpen}
                                onSelect={(c) => {
                                    setSelectedCashier(c);
                                    setPinValue('');
                                    setLoginError(null);
                                }}
                                onRetry={reload}
                            />
                            {selectedCashier && (
                                <PinKeypad
                                    pinValue={pinValue}
                                    onPinChange={setPinValue}
                                    headerText={
                                        isSubmitting
                                            ? 'Comprobando...'
                                            : (loginError || 'Introduce PIN de 6 dígitos')
                                    }
                                    showBackButton={isClosable}
                                    onBack={() => setView('menu')}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}